import { AsyncPipe, NgClass, NgTemplateOutlet, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TrackByFunction, inject } from '@angular/core';

import {
    CrmPromoIdsMap,
    EventModel,
    EventOption,
    EventOptionGroup,
    EventParticipant,
    OptionGroup2,
} from '@frontend/sports/betting-offer/feature/model';
import { ShortNameModule, ShortTeamNamesService } from '@frontend/sports/betting-offer/feature/short-team-names';
import { MarketGroupingConfig, MarketSliderConfiguration, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { DsDivider } from '@frontend/ui/divider';
import { RxFor } from '@rx-angular/template/for';
import { orderBy } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';

import { SHEET_VIEW_OPEN } from '../sheet-view-core/sheet-view.token';
import { MarketSliderComponent } from './component/market-slider/market-slider.component';
import { MarketUtilsService } from './component/market-slider/service/market-slider-utils.service';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionAttributeNameStrategy } from './option-attribute-name.strategy';
import { OptionGroupTrackingService } from './option-group-tracking.service';
import { OptionGroup, OptionGroupService } from './option-group.service';
import { OptionNameStrategy } from './option-name.strategy';
import { OptionPanelBottomActionComponent, OptionPanelBottomActionState, ShowMoreData } from './option-panel-bottom-action.component';
import { OptionComponent } from './option.component';

@Component({
    selector: 'ms-spread-option-group',
    templateUrl: './spread-option-group.component.html',
    styleUrl: './spread-option-group.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: OptionNameStrategy, useClass: OptionAttributeNameStrategy }],
    imports: [
        RxFor,
        NgClass,
        NgTemplateOutlet,
        SlicePipe,
        AsyncPipe,
        OptionComponent,
        MarketHelpComponent,
        OptionPanelBottomActionComponent,
        MarketSliderComponent,
        DsDivider,
    ],
})
export class SpreadOptionGroupComponent implements OnChanges {
    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() optionGroup: EventOptionGroup;
    @Input() showMarketHelp: boolean;
    @Input() crmPromoIdsMap: CrmPromoIdsMap;
    @Input() eventHelpText?: string;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;

    @Output() showAllRequested = new EventEmitter<void>();

    private readonly marketUtilsService = inject(MarketUtilsService);
    private readonly marketSliderConfig = inject(MarketSliderConfiguration);
    private readonly marketGroupingConfig = inject(MarketGroupingConfig);
    private readonly optionGroupService = inject(OptionGroupService);
    private readonly shortTeamNamesService = inject(ShortTeamNamesService);
    public readonly sitecore = inject(Sitecore);
    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));
    private optionGroupTrackingService = inject(OptionGroupTrackingService);

    options: EventOption[] = [];
    optionGroups: OptionGroup2[];
    expanded = false;
    showMoreLessButtonVisible = false;
    tupleClass: string;
    numberOfOptions = 0;
    rows = 0;
    private maxNumberOfVisibleOptions = this.marketGroupingConfig.spreadGroupMaxNumberOfLines;
    visibleOptionsLimit: number;
    showMoreData: ShowMoreData;

    sliderSteps: number[] = [];
    centerValue = 0;
    disableSlider = false;
    firstParticipant = '';
    secondParticipant = '';
    rightLabel?: string;
    leftLabel?: string;
    isSliderVisibleWithoutShowMore = false;
    enableForSpread = false;

    currentValue$ = new BehaviorSubject<number>(0);

    gridClasses = new Map<number, string>([
        [2, 'double'],
        [3, 'triple'],
        [4, 'quadruple'],
        [5, 'quintuple'],
        [6, 'sextuple'],
    ]);

    ngOnChanges(changes: SimpleChanges): void {
        if (this.optionsLimitOverride) {
            this.maxNumberOfVisibleOptions = this.optionsLimitOverride;
        }
        this.updateEnableForSpread();
        if (changes.optionGroup?.currentValue) {
            this.setOptionGroups();
            this.isSliderVisibleWithoutShowMore =
                this.optionGroups.length - 1 < this.marketGroupingConfig.spreadGroupMaxNumberOfLines && this.enableForSpread;

            if (this.enableForSpread) {
                this.sliderSteps = this.marketUtilsService.getSteps(this.optionGroup);
                this.centerValue = this.marketUtilsService.getBalanceLine(this.optionGroup);
                this.disableSlider = !this.optionGroup.online;

                if (this.sliderSteps.length) {
                    const [first, last] = [this.sliderSteps[0], this.sliderSteps.at(-1)];
                    this.leftLabel = this.formatLabel(first);
                    this.rightLabel = this.formatLabel(last!);
                }
            } else {
                this.sliderSteps = [];
                this.centerValue = 0;
                this.disableSlider = false;
            }
        }

        this.setParticipants();
        this.numberOfOptions = this.optionGroups[0]?.options.length;
        this.updateOptionAttr();

        this.showMoreLessButtonVisible = this.optionGroups.length > this.maxNumberOfVisibleOptions;
        this.tupleClass = this.getTupleClass();

        if (changes.event || changes.optionGroup || changes.optionsLimitOverride) {
            const totalOptions = this.optionGroups.length;
            this.showMoreData = {
                totalCount: totalOptions,
                initialVisibleCount: this.maxNumberOfVisibleOptions,
                pageSize: totalOptions,
            };
        }
        this.calculateMaxNumberOfVisibleOptions();
    }

    setOptionGroups(): void {
        this.optionGroups = orderBy(
            this.optionGroupService.getOptionGroups(this.optionGroup, (option) => option.optionGroupId),
            ['optionAttribute'],
        );

        this.optionGroups = this.optionGroups.map((og) =>
            this.optionGroupService.orderBasedOnParticipants(og, this.event.participants, this.event.viewType),
        );
    }

    trackGroup: TrackByFunction<OptionGroup | OptionGroup2> = (index, group) => {
        return group.attribute;
    };

    trackOption: TrackByFunction<EventOption | undefined> = (index, option) => {
        return option ? option.id : `empty-${index}`;
    };

    onShowMoreLessButtonToggle(): void {
        this.expanded = !this.expanded;
        this.visibleOptionsLimit = this.expanded ? this.optionGroups.length : this.maxNumberOfVisibleOptions;
        this.rows = Math.min(this.optionGroups.length, this.visibleOptionsLimit);
    }

    onShowMore({ state }: { state: OptionPanelBottomActionState; visibleCount: number }): void {
        this.expanded = state !== 'collapsed';
        this.calculateMaxNumberOfVisibleOptions();

        this.optionGroupTrackingService.trackCollapseExpandClick(
            this.event.sport.name,
            this.expanded ? 'expand' : 'collapse',
            this.event.participants,
            this.optionGroup.name,
            this.optionGroup.name,
        );

        if (this.enableForSpread && state === 'collapsed') {
            this.setParticipants();
        }
    }

    setSliderCurrentValue(value: number): void {
        this.currentValue$.next(value);
    }

    get visibleOptionGroups(): OptionGroup2[] {
        if (!this.enableForSpread) {
            return this.optionGroups;
        }

        return this.filterOptionGroupsBySelectedSpread(this.optionGroups, this.currentValue$.value);
    }

    get effectiveVisibleLimit(): number {
        if (this.enableForSpread && this.isSliderVisibleWithoutShowMore) {
            return this.visibleOptionGroups.length;
        }
        return this.visibleOptionsLimit;
    }

    get spreadVisibility() {
        const canShowSpread = this.enableForSpread && !this.isInSheetView;
        return {
            showSlider: canShowSpread,
            showExpandedView: canShowSpread && this.expanded && this.showMoreLessButtonVisible,
        };
    }

    protected setParticipants(): void {
        this.firstParticipant = this.getParticipantName(this.event?.participants[0]);
        this.secondParticipant = this.getParticipantName(this.event?.participants[1]);
    }

    private getParticipantName(participant: EventParticipant): string {
        if (!participant) {
            return '';
        }

        if (this.shortTeamNamesService.shouldUseShortName(participant, this.event.sport.id, this.event.league.id, ShortNameModule.Spread)) {
            return participant.shortName!;
        }

        return participant.name;
    }

    protected updateOptionAttr(): void {
        if (!this.numberOfOptions || this.numberOfOptions <= 2) {
            return;
        }
        this.optionGroups.forEach((group: OptionGroup2) => {
            if (group.options[0]?.optionAttribute && group.options[2]?.optionAttribute) {
                group.options[1] && (group.options[1].optionAttribute = group.options[0].optionAttribute + '/' + group.options[2].optionAttribute);
            }
        });
    }

    private calculateMaxNumberOfVisibleOptions(): void {
        this.visibleOptionsLimit = this.isInSheetView || this.expanded ? this.optionGroups.length : this.maxNumberOfVisibleOptions;
        this.rows = Math.min(this.optionGroups.length, this.visibleOptionsLimit);
    }

    private getTupleClass(): string {
        return this.gridClasses.get(this.numberOfOptions) ?? '';
    }

    private filterOptionGroupsBySelectedSpread(optionGroups: OptionGroup2[], selectedSpread: number): OptionGroup2[] {
        return optionGroups.reduce<OptionGroup2[]>((result, group) => {
            const options = group.options;
            if (!options?.length) return result;

            const team1Name = options[0]?.name?.split(' ').slice(0, -1).join(' ') || '';

            const team1Option = options.find(
                (opt) => opt?.optionAttribute !== undefined && opt.name?.startsWith(team1Name) && +opt.optionAttribute === selectedSpread,
            );

            const team2Option = options.find(
                (opt) => opt?.optionAttribute !== undefined && !opt.name?.startsWith(team1Name) && +opt.optionAttribute === -selectedSpread,
            );

            if (team1Option && team2Option) {
                result.push({ ...group, options: [team1Option, team2Option] });
            }

            return result;
        }, []);
    }

    private updateEnableForSpread(): void {
        this.enableForSpread =
            this.marketSliderConfig.enableForSpread &&
            this.marketSliderConfig.activationThreshold <= (this.optionGroup.options.length ?? 0) &&
            !this.isInSheetView;
    }

    private formatLabel(value: number): string {
        return value > 0 ? `+${value}` : `${value}`;
    }
}
